// Turning on notifications that arrive when the site is closed.
//
// The iOS rule that shapes all of this: Apple delivers web push ONLY to a site
// that has been added to the Home Screen and opened from that icon. In a normal
// Safari tab the APIs are missing entirely, so the honest thing is to detect
// that and say "install it first" rather than show a button that cannot work.
import axios from 'axios';

export const supportsPush = () =>
  'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

// Running from the Home Screen icon rather than a browser tab.
export const isInstalled = () =>
  window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true;

export const isIOS = () =>
  /iphone|ipad|ipod/i.test(navigator.userAgent) ||
  // iPadOS reports itself as a Mac; the touch points give it away.
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

// Why the button can't work, in words worth showing someone. null = it can.
export function pushBlockedReason() {
  if (isIOS() && !isInstalled()) {
    return 'On iPhone, notifications only work once this site is added to your Home Screen. Tap the Share button, choose "Add to Home Screen", then open it from that icon and come back here.';
  }
  if (!supportsPush()) {
    return 'This browser cannot deliver notifications. Chrome on Android works as it is; on iPhone use Safari with the site added to your Home Screen.';
  }
  if (Notification.permission === 'denied') {
    return 'Notifications are blocked for this site in your browser settings. You will have to allow them there first — the site cannot ask again once it has been refused.';
  }
  return null;
}

// The server hands out the public VAPID key; the browser wants it as bytes.
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map(c => c.charCodeAt(0)));
}

const registerWorker = () => navigator.serviceWorker.register('/sw.js', { scope: '/' });

// Registers the worker on every visit, not just when someone turns
// notifications on — it is also what caches the app for offline use, and a
// technician who never taps the notification button still deserves that.
export function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return;
  window.addEventListener('load', () => {
    registerWorker().catch(() => { /* unsupported or blocked; the app still works */ });
  });
}

// Signing out clears cached API responses, so the next person to pick up a
// shared phone can't page through the last user's jobs offline.
export async function clearOfflineData() {
  try {
    const registration = await navigator.serviceWorker?.getRegistration('/');
    registration?.active?.postMessage({ type: 'clear-api-cache' });
  } catch { /* nothing cached to clear */ }
}

// Subscribes this device. Throws with something readable on failure — the
// caller shows it, because a silent no-op here is indistinguishable from the
// feature being broken.
export async function enablePush(authHeaders) {
  const blocked = pushBlockedReason();
  if (blocked) throw new Error(blocked);

  const { data } = await axios.get('/api/push/key');
  if (!data.enabled || !data.key) {
    throw new Error('Notifications are not switched on for this site yet — the VAPID keys are missing in Render.');
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new Error('You did not allow notifications, so nothing will be sent.');

  const registration = await registerWorker();
  // Without this the very first subscribe can run against a worker that is
  // installed but not yet controlling the page, and silently does nothing.
  await navigator.serviceWorker.ready;

  const existing = await registration.pushManager.getSubscription();
  const subscription = existing || await registration.pushManager.subscribe({
    // Required to be true: a push that shows nothing to the user is not allowed.
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(data.key),
  });

  await axios.post('/api/push/subscribe', subscription.toJSON(), authHeaders);
  return true;
}

export async function disablePush(authHeaders) {
  if (!supportsPush()) return;
  const registration = await navigator.serviceWorker.getRegistration('/');
  const subscription = await registration?.pushManager.getSubscription();
  if (!subscription) return;
  await axios.post('/api/push/unsubscribe', { endpoint: subscription.endpoint }, authHeaders).catch(() => {});
  await subscription.unsubscribe().catch(() => {});
}

// Is this specific device subscribed right now? Asks the browser rather than
// the server, because the server knows about every device and the question
// being asked is about the one in your hand.
export async function isPushOnThisDevice() {
  if (!supportsPush() || Notification.permission !== 'granted') return false;
  try {
    const registration = await navigator.serviceWorker.getRegistration('/');
    return Boolean(await registration?.pushManager.getSubscription());
  } catch {
    return false;
  }
}
